/**
 * Mapping between menu item IDs and required permissions.
 * Items not listed here are visible to all users.
 * Admin users bypass all permission checks.
 */
import { PermissionCodes, PermissionCode } from './permissionCodes';

export interface MenuPermissionConfig {
	/** Required permissions (user needs at least one) */
	permissions?: PermissionCode[];
	/** If true, only Admin users can see this item */
	adminOnly?: boolean;
}

/**
 * Menu ID to permission mapping
 */
export const MenuPermissionMapping: Record<string, MenuPermissionConfig> = {
	// Dashboard - always visible
	dashboard: {},

	// Organizations - always visible (but moving to settings)
	organizations: {},

	// HR / Talents section - requires talents permission
	hr: { permissions: [PermissionCodes.TALENTS] },
	aiSearch: { permissions: [PermissionCodes.TALENTS] },
	classicSearch: { permissions: [PermissionCodes.TALENTS] },
	myTalent: { permissions: [PermissionCodes.TALENTS] },
	savedSearches: { permissions: [PermissionCodes.TALENTS] },

	// PM / Projects section - requires projects permission
	pm: { permissions: [PermissionCodes.PROJECTS] },
	projects: { permissions: [PermissionCodes.PROJECTS] },
	milestones: { permissions: [PermissionCodes.PROJECTS] },
	timesheets: { permissions: [PermissionCodes.PROJECTS] },

	// Legal / Contracts section - requires contracts permission
	legal: { permissions: [PermissionCodes.CONTRACTS] },
	contracts: { permissions: [PermissionCodes.CONTRACTS] },

	// Finance section - requires finance permission
	finance: { permissions: [PermissionCodes.FINANCE] },
	invoices: { permissions: [PermissionCodes.FINANCE] },

	// ========== SETTINGS AREA (Admin only) ==========
	// Main settings entry point
	settings: { adminOnly: true },

	// Organization settings
	organization: { adminOnly: true },
	profile: { adminOnly: true },
	billing: { adminOnly: true },

	// Legal settings
	'master-agreement': { adminOnly: true },
	templates: { adminOnly: true },

	// Team management - Admin only
	team: { adminOnly: true },
	members: { adminOnly: true },

	// Subscription settings - Admin only
	subscription: { adminOnly: true },
	plan: { adminOnly: true },
};

/**
 * Check if a menu item should be visible based on permissions
 * @param menuId The menu item ID
 * @param isAdmin Whether the user is an Admin
 * @param userPermissions The user's permissions array
 * @returns Whether the menu item should be visible
 */
export const canShowMenuItem = (
	menuId: string,
	isAdmin: boolean,
	userPermissions: PermissionCode[],
): boolean => {
	// Admin sees everything
	if (isAdmin) return true;

	const config = MenuPermissionMapping[menuId];

	// No config means always visible
	if (!config) return true;

	// Admin-only items hidden for non-admins
	if (config.adminOnly) return false;

	// No permission requirement means visible
	if (!config.permissions || config.permissions.length === 0) return true;

	// Check if user has at least one required permission
	return config.permissions.some((p) => userPermissions.includes(p));
};
